import { useEffect, useRef, useState } from "react";

type Props = {
  label: string;
  aide?: string;
  onCapture: (fichier: File) => void;
  // Autorise le téléversement en plus de la caméra. Faux pour le selfie :
  // c'est lui qui relie la personne au document, il doit être pris sur place.
  autoriserFichier?: boolean;
  facingMode?: "user" | "environment";
};

// Prise de photo par la caméra, avec repli sur le téléversement quand c'est
// permis. La contrainte « photo obligatoire » reste une contrainte
// d'interface : elle décourage le geste facile, elle ne remplace pas l'examen
// du dossier par un administrateur.
export default function CapturePhoto({
  label,
  aide,
  onCapture,
  autoriserFichier = false,
  facingMode = "user",
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fluxRef = useRef<MediaStream | null>(null);
  const [actif, setActif] = useState(false);
  const [apercu, setApercu] = useState<string | null>(null);
  const [erreur, setErreur] = useState("");

  const arreter = () => {
    fluxRef.current?.getTracks().forEach((t) => t.stop());
    fluxRef.current = null;
    setActif(false);
  };

  useEffect(() => arreter, []);

  const demarrer = async () => {
    setErreur("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setErreur("Votre navigateur ne donne pas accès à la caméra.");
      return;
    }
    try {
      const flux = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: false,
      });
      fluxRef.current = flux;
      setActif(true);
      // La balise vidéo n'existe qu'une fois `actif` passé à vrai.
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = flux;
          videoRef.current.play().catch(() => undefined);
        }
      }, 0);
    } catch (e: any) {
      setErreur(
        e?.name === "NotAllowedError"
          ? "Accès à la caméra refusé. Autorisez-le dans votre navigateur."
          : "Caméra indisponible sur cet appareil."
      );
    }
  };

  const prendre = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const fichier = new File([blob], `${label.replace(/\s+/g, "-")}.jpg`, {
          type: "image/jpeg",
        });
        setApercu(URL.createObjectURL(blob));
        onCapture(fichier);
        arreter();
      },
      "image/jpeg",
      0.9
    );
  };

  return (
    <div className="mb-3">
      <label className="modal-investissement-description d-block mb-1">
        {label}
      </label>
      {aide && (
        <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 6 }}>
          {aide}
        </div>
      )}

      {apercu && (
        <div className="mb-2">
          <img
            src={apercu}
            alt={label}
            style={{
              maxWidth: 220,
              borderRadius: 8,
              border: "1px solid #c4c4c4",
            }}
          />
        </div>
      )}

      {actif && (
        <div className="mb-2">
          <video
            ref={videoRef}
            playsInline
            muted
            style={{
              width: "100%",
              maxWidth: 360,
              borderRadius: 8,
              background: "#000",
            }}
          />
        </div>
      )}

      <div className="d-flex flex-wrap gap-2">
        {!actif && (
          <button
            type="button"
            className="btn tb-action-btn"
            onClick={demarrer}
          >
            {apercu ? "Reprendre la photo" : "Prendre une photo"}
          </button>
        )}
        {actif && (
          <>
            <button
              type="button"
              className="btn tb-action-btn tb-action-btn-retirer"
              onClick={prendre}
            >
              Capturer
            </button>
            <button type="button" className="btn tb-action-btn" onClick={arreter}>
              Annuler
            </button>
          </>
        )}
        {autoriserFichier && !actif && (
          <label className="btn tb-action-btn mb-0">
            Choisir un fichier
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setApercu(URL.createObjectURL(f));
                onCapture(f);
              }}
            />
          </label>
        )}
      </div>

      {erreur && (
        <div className="alert alert-danger sia-alert-danger closer mt-2">
          {erreur}
          {autoriserFichier ? "" : " La photo est obligatoire pour cette étape."}
        </div>
      )}
    </div>
  );
}
